/**
 * `LearnedPolicy` — a trained checkpoint plugged into the existing bot seam.
 *
 * ===========================================================================
 * THE SEAM
 * ===========================================================================
 *
 * ../bot/bot declares `Policy` as `observe(context) -> CarInput`, and
 * `BotDirector` already owns prediction, roles, the reaction queue and input
 * sanitization. A trained network therefore needs nothing new: construct one of
 * these from a checkpoint and pass it as `BotConfig.policy`. Nothing else in
 * the bot layer changes, and the `'learned'` difficulty tier stops throwing the
 * moment a caller supplies one.
 *
 * ===========================================================================
 * IT MUST HOLD ITS ACTION
 * ===========================================================================
 *
 * The policy was trained with a frame skip: one decision every
 * `env.DEFAULT_FRAME_SKIP` ticks, held for the whole window. `BotDirector`
 * calls `observe` every tick at 120 Hz. Re-sampling every tick is not a
 * harmless speed-up — it is a DIFFERENT policy from the one that was trained:
 * a flip needs a jump press held across ticks, and an action stream redrawn at
 * 120 Hz from a stochastic head cancels its own inputs before any mechanic can
 * commit. So the action is latched and re-decided on the same cadence training
 * used. `ACTION_HOLD_TICKS` is asserted equal to the env's frame skip by
 * `test/rl/policy.test.ts`; it is duplicated rather than imported so that
 * shipping a policy does not drag `RLEnv` and its `World` into the bundle.
 *
 * ===========================================================================
 * GREEDY OR SAMPLED
 * ===========================================================================
 *
 * Greedy (argmax) by default: it is deterministic, it is what a player wants
 * from a bot, and it is the mode the determinism test pins. Sampling is the
 * mode training used, and is kept because a greedy policy in a symmetric state
 * is exploitable — it makes exactly the same mistake every time.
 *
 * ===========================================================================
 * NO ALLOCATION PER TICK
 * ===========================================================================
 *
 * The observation buffer, the forward result, the probability vector and the
 * returned `CarInput` are all owned by the instance. The returned input is a
 * COPY of the action table's entry rather than the entry itself: the table is
 * frozen, and `BotDirector` runs every policy's output through `sanitizeInput`,
 * which writes to its argument.
 */

import { neutralInput, type CarInput } from '../physics/car-state';
import { Rng } from '../physics/rng';
import type { Policy, PolicyContext } from '../bot/bot';
import { ACTION_COUNT, actionInput } from './actions';
import { logSoftmax, makeForwardResult, MlpPolicy, sampleCategorical } from './nn';
import { buildObservation, makeObservation } from './obs';
import { CHECKPOINT_VERSION, OBS_SIZE } from './types';
import type { ActionId, Checkpoint, ForwardResult, Observation } from './types';

/**
 * Ticks one decision is held for. Must equal `DEFAULT_FRAME_SKIP` in ./env —
 * see the header, and `test/rl/policy.test.ts` for the assertion.
 */
export const ACTION_HOLD_TICKS = 8;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'number' && Number.isFinite(v));

/**
 * Is this object a checkpoint THIS build can run?
 *
 * Structure, version, observation width and action count are all checked. The
 * last two are the ones that matter: a checkpoint trained against a different
 * observation layout or a reordered action table loads without complaint,
 * initializes cleanly, runs at full speed and plays like noise, and there is
 * nothing in that failure to point at. See the header of ./types.
 */
export const isCheckpointCompatible = (cp: unknown): cp is Checkpoint => {
  if (typeof cp !== 'object' || cp === null) return false;
  const c = cp as Partial<Checkpoint>;
  if (c.version !== CHECKPOINT_VERSION) return false;
  if (c.obsSize !== OBS_SIZE || c.actionCount !== ACTION_COUNT) return false;
  if (!isNumberArray(c.hiddenSizes) || c.hiddenSizes.some((h) => h <= 0)) return false;
  if (!isNumberArray(c.parameters) || c.parameters.length === 0) return false;
  if (typeof c.stats !== 'object' || c.stats === null) return false;
  const stats = c.stats;
  if (typeof stats.iteration !== 'number' || typeof stats.totalSteps !== 'number') return false;
  if (typeof stats.totalSeconds !== 'number' || !Array.isArray(stats.history)) return false;
  if (c.optimizer !== undefined) {
    const o = c.optimizer;
    if (typeof o !== 'object' || o === null) return false;
    if (!isNumberArray(o.m) || !isNumberArray(o.v) || typeof o.step !== 'number') return false;
    if (o.m.length !== c.parameters.length || o.v.length !== c.parameters.length) return false;
  }
  return true;
};

/**
 * `isCheckpointCompatible`, but it names what is wrong.
 *
 * A boolean predicate is the right shape for a type guard and the wrong shape
 * for a load failure: "checkpoint rejected" with no reason sends the user
 * looking at the wrong thing. Every rejection below says which field disagreed
 * and what was expected.
 */
export const assertCheckpointCompatible = (cp: unknown): Checkpoint => {
  if (typeof cp !== 'object' || cp === null) {
    throw new Error(`checkpoint must be an object, got ${cp === null ? 'null' : typeof cp}`);
  }
  const c = cp as Partial<Checkpoint>;
  if (c.version !== CHECKPOINT_VERSION) {
    throw new Error(`checkpoint version ${String(c.version)}, expected ${CHECKPOINT_VERSION}`);
  }
  if (c.obsSize !== OBS_SIZE) {
    throw new Error(`checkpoint obsSize ${String(c.obsSize)}, this build expects ${OBS_SIZE}`);
  }
  if (c.actionCount !== ACTION_COUNT) {
    throw new Error(
      `checkpoint actionCount ${String(c.actionCount)}, this build expects ${ACTION_COUNT}`
    );
  }
  if (!isCheckpointCompatible(cp)) {
    throw new Error('checkpoint is structurally invalid (parameters, hiddenSizes or stats)');
  }
  return cp;
};

// ---------------------------------------------------------------------------
// The policy
// ---------------------------------------------------------------------------

export interface LearnedPolicyOptions {
  /** Argmax over the logits (default) rather than a draw from the softmax. */
  greedy?: boolean;
  /** Seed for the sampling generator. Unused in greedy mode. */
  seed?: number;
  /** Ticks one decision is held. Defaults to the training frame skip. */
  holdTicks?: number;
}

export class LearnedPolicy implements Policy {
  readonly name = 'learned';
  readonly greedy: boolean;
  readonly holdTicks: number;
  /** Whatever label the checkpoint carried, for a debug overlay. */
  readonly label: string;

  private readonly net: MlpPolicy;
  private readonly obs: Observation = makeObservation();
  private readonly out: ForwardResult;
  private readonly logProbs: Float64Array;
  private readonly probs: Float64Array;
  private readonly input: CarInput = neutralInput();
  private readonly rng: Rng;
  private readonly seed: number;

  private action: ActionId = 0;
  /** Tick at which the latched action expires. -1 means "decide now". */
  private expiresAtTick = -1;

  constructor(cp: Checkpoint, options: LearnedPolicyOptions = {}) {
    assertCheckpointCompatible(cp);
    this.greedy = options.greedy ?? true;
    this.holdTicks = Math.max(1, Math.floor(options.holdTicks ?? ACTION_HOLD_TICKS));
    this.label = cp.label ?? '';
    this.seed = options.seed ?? 0;
    this.rng = new Rng(this.seed);

    this.net = new MlpPolicy(cp.obsSize, cp.actionCount, cp.hiddenSizes, 0);
    this.net.setParameters(cp.parameters);
    this.out = makeForwardResult(cp.actionCount);
    this.logProbs = new Float64Array(cp.actionCount);
    this.probs = new Float64Array(cp.actionCount);
  }

  reset(): void {
    this.expiresAtTick = -1;
    this.action = 0;
    this.rng.state = this.seed >>> 0;
  }

  observe(ctx: PolicyContext): CarInput {
    if (this.expiresAtTick < 0 || ctx.tick >= this.expiresAtTick) {
      buildObservation(ctx.view, ctx.carIndex, this.obs);
      this.action = this.act(this.obs);
      this.expiresAtTick = ctx.tick + this.holdTicks;
    }
    return copyInput(actionInput(this.action), this.input);
  }

  /**
   * The action for one observation. Exposed so a test can pin determinism, and
   * so a debug overlay can show what the policy wanted without stepping it.
   */
  act(obs: Observation): ActionId {
    this.net.forward(obs, this.out);
    if (this.greedy) return argmax(this.out.logits);
    logSoftmax(this.out.logits, this.logProbs);
    for (let i = 0; i < this.probs.length; i++) this.probs[i] = Math.exp(this.logProbs[i]);
    return sampleCategorical(this.probs, this.rng);
  }

  /** The state value the critic assigns to an observation. For a HUD. */
  value(obs: Observation): number {
    return this.net.forward(obs, this.out).value;
  }
}

/** Highest-scoring index, ties to the lowest — so greedy mode is a pure
 *  function of the observation and the parameters. */
const argmax = (values: Float64Array): number => {
  let best = 0;
  let bestValue = values[0];
  for (let i = 1; i < values.length; i++) {
    if (values[i] > bestValue) {
      bestValue = values[i];
      best = i;
    }
  }
  return best;
};

const copyInput = (from: Readonly<CarInput>, out: CarInput): CarInput => {
  out.throttle = from.throttle;
  out.steer = from.steer;
  out.pitch = from.pitch;
  out.yaw = from.yaw;
  out.roll = from.roll;
  out.jump = from.jump;
  out.boost = from.boost;
  out.handbrake = from.handbrake;
  return out;
};
